// src/pages/SendPushNotificationPage.js
// Updated: 08/29/2025 03:44 PM

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { db, storage, functions } from '../services/firebase';
import { httpsCallable } from 'firebase/functions';
import {
  collection, query, where, onSnapshot,
  doc, getDoc,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  Container, Typography, Box, Paper, TextField, Button,
  CircularProgress, Alert, FormControl, InputLabel, Select,
  MenuItem, OutlinedInput, Checkbox, ListItemText,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';


function SendPushNotificationPage() {
  const { userProfile, churchProfile } = useAuth(); // MODIFIED: Added churchProfile
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [recipientType, setRecipientType] = useState('all');
  const [allGroups, setAllGroups] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [allLists, setAllLists] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedRoleNames, setSelectedRoleNames] = useState([]);
  const [selectedListId, setSelectedListId] = useState('');
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentUrl, setAttachmentUrl] = useState('');
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [scheduleTime, setScheduleTime] = useState(null);

  useEffect(() => {
    if (!userProfile?.churchId) return;

    // Fetch groups
    const groupsQuery = query(collection(db, `churches/${userProfile.churchId}/groups`));
    const unsubscribeGroups = onSnapshot(groupsQuery, (snapshot) => {
      setAllGroups(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    // Fetch users
    const usersQuery = query(collection(db, 'users'), where('churchId', '==', userProfile.churchId));
    const unsubscribeUsers = onSnapshot(usersQuery, (snapshot) => {
      setAllUsers(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    // Fetch roles
    setAllRoles(['Member', 'Leadership', 'Church Administrator']);

    // Fetch Distribution Lists
    const listsQuery = query(
      collection(db, `churches/${userProfile.churchId}/distributionLists`)
    );
    const unsubscribeLists = onSnapshot(listsQuery, (snapshot) => {
      setAllLists(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    return () => {
      unsubscribeGroups();
      unsubscribeUsers();
      unsubscribeLists();
    };
  }, [userProfile?.churchId]);

  const handleFileChange = (e) => {
    setAttachmentFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    setSuccess('');

    let finalAttachmentUrl = attachmentUrl;
    if (attachmentFile) {
      try {
        const fileRef = ref(storage, `notification_attachments/${Date.now()}_${attachmentFile.name}`);
        const snapshot = await uploadBytes(fileRef, attachmentFile);
        finalAttachmentUrl = await getDownloadURL(snapshot.ref);
      } catch (err) {
        setError('Failed to upload attachment.');
        setSending(false);
        return;
      }
    }

    let recipientIds = [];
    // --- FIX START: Simplify the recipientIds logic ---
    if (recipientType === 'groups') {
      recipientIds = selectedGroupIds; // Send the group IDs directly
    } else if (recipientType === 'users') {
      recipientIds = selectedUserIds;
    } else if (recipientType === 'roles') {
      recipientIds = selectedRoleNames;
    } else if (recipientType === 'distribution_list') {
      recipientIds = [selectedListId];
    }
    // --- FIX END ---

    try {
      if (scheduleTime && scheduleTime > new Date()) {
        const scheduleCustomNotification = httpsCallable(functions, 'scheduleCustomNotification');
        const result = await scheduleCustomNotification({
          churchId: userProfile.churchId,
          title,
          body,
          recipientType,
          recipientIds,
          attachmentUrl: finalAttachmentUrl,
          scheduleTimestamp: scheduleTime.toISOString(),
          churchTimeZone: churchProfile?.timeZone || 'America/New_York',
        });
        setSuccess(result.data.message);
      } else {
        const sendCustomNotification = httpsCallable(functions, 'sendCustomNotification');
        const result = await sendCustomNotification({
          churchId: userProfile.churchId,
          title,
          body,
          recipientType,
          recipientIds,
          attachmentUrl: finalAttachmentUrl,
        });
        setSuccess(result.data.message);
      }

      setTitle('');
      setBody('');
      setAttachmentFile(null);
      setAttachmentUrl('');
      setScheduleTime(null);

    } catch (err) {
      console.error('Error sending/scheduling notification:', err);
      setError(`Failed to process notification: ${err.message}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="md">
        <Paper sx={{ p: 4, mt: 3 }}>
          <Typography variant="h4" gutterBottom>Send Custom Notification</Typography>
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Notification Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Message Body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              margin="normal"
              multiline
              rows={4}
              required
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>Recipient Type</InputLabel>
              <Select value={recipientType} label="Recipient Type" onChange={(e) => setRecipientType(e.target.value)}>
                <MenuItem value="all">All Church Members</MenuItem>
                <MenuItem value="roles">Specific Roles</MenuItem>
                <MenuItem value="groups">Members of Specific Groups</MenuItem>
                <MenuItem value="users">Specific Members</MenuItem>
                <MenuItem value="distribution_list">Distribution List</MenuItem>
              </Select>
            </FormControl>

            {recipientType === 'roles' && (
              <FormControl fullWidth margin="normal">
                <InputLabel>Roles</InputLabel>
                <Select
                  multiple
                  value={selectedRoleNames}
                  onChange={(e) => setSelectedRoleNames(e.target.value)}
                  input={<OutlinedInput label="Roles" />}
                  renderValue={(selected) => selected.join(', ')}
                >
                  {allRoles.map((role) => (
                    <MenuItem key={role} value={role}>
                      <Checkbox checked={selectedRoleNames.indexOf(role) > -1} />
                      <ListItemText primary={role} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {recipientType === 'groups' && (
               <FormControl fullWidth margin="normal">
               <InputLabel>Groups</InputLabel>
               <Select
                 multiple
                 value={selectedGroupIds}
                 onChange={(e) => setSelectedGroupIds(e.target.value)}
                 input={<OutlinedInput label="Groups" />}
                 renderValue={(selected) => selected.map(id => allGroups.find(g => g.id === id)?.name).join(', ')}
               >
                 {allGroups.map((group) => (
                   <MenuItem key={group.id} value={group.id}>
                     <Checkbox checked={selectedGroupIds.indexOf(group.id) > -1} />
                     <ListItemText primary={group.name} />
                   </MenuItem>
                 ))}
               </Select>
             </FormControl>
            )}

            {recipientType === 'users' && (
               <FormControl fullWidth margin="normal">
               <InputLabel>Members</InputLabel>
               <Select
                 multiple
                 value={selectedUserIds}
                 onChange={(e) => setSelectedUserIds(e.target.value)}
                 input={<OutlinedInput label="Members" />}
                 renderValue={(selected) => selected.map(id => {
                   const user = allUsers.find(u => u.id === id);
                   return user ? `${user.firstName} ${user.lastName}` : '';
                 }).join(', ')}
               >
                 {allUsers.map((user) => (
                   <MenuItem key={user.id} value={user.id}>
                     <Checkbox checked={selectedUserIds.indexOf(user.id) > -1} />
                     <ListItemText primary={`${user.firstName} ${user.lastName}`} />
                   </MenuItem>
                 ))}
               </Select>
             </FormControl>
            )}
            
            {recipientType === 'distribution_list' && (
              <FormControl fullWidth margin="normal">
                <InputLabel>Select List</InputLabel>
                <Select
                  value={selectedListId}
                  label="Select List"
                  onChange={(e) => setSelectedListId(e.target.value)}
                >
                  {allLists.map((list) => (
                    <MenuItem key={list.id} value={list.id}>{list.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              fullWidth
              label="Attachment URL (Optional)"
              value={attachmentUrl}
              onChange={(e) => setAttachmentUrl(e.target.value)}
              margin="normal"
              helperText="Paste a link to an external image, document, or page."
            />

            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadIcon />}
              sx={{ mt: 1, mb: 2 }}
            >
              Upload Attachment (Optional)
              <input type="file" hidden onChange={handleFileChange} />
            </Button>
            {attachmentFile && <Typography variant="body2">{attachmentFile.name}</Typography>}
            
            <Box sx={{ my: 2 }}>
                <DateTimePicker
                    label="Schedule for Later (Optional)"
                    value={scheduleTime}
                    onChange={(newValue) => setScheduleTime(newValue)}
                    slots={{ textField: (params) => <TextField {...params} fullWidth /> }}
                    disablePast
                    enableAccessibleFieldDOMStructure={false}
                />
            </Box>


            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            
            <Button type="submit" variant="contained" size="large" disabled={sending} sx={{ mt: 2 }}>
              {sending ? <CircularProgress size={24} /> : (scheduleTime ? 'Schedule Notification' : 'Send Notification Now')}
            </Button>
          </Box>
        </Paper>
      </Container>
    </LocalizationProvider>
  );
}

export default SendPushNotificationPage;